(() => {
  const initialize = (section) => {
    if (section.dataset.collectionsInitialized === 'true') return;

    const track = section.querySelector('[data-collections-track]');
    const viewport = section.querySelector('[data-collections-viewport]');
    const originalCards = [...section.querySelectorAll('[data-collection-card]')];
    const dots = [...section.querySelectorAll('[data-collections-dot]')];
    if (!track || !viewport || !originalCards.length) return;

    section.dataset.collectionsInitialized = 'true';
    const cardCount = originalCards.length;
    const cloneCount = Math.min(2, cardCount);
    let activeIndex = Math.max(0, originalCards.findIndex((card) => card.classList.contains('is-active')));
    let position = cloneCount + activeIndex;
    let animationLocked = false;
    let fallbackTimer;

    originalCards.forEach((card, index) => { card.dataset.logicalIndex = index; });

    const makeClone = (card) => {
      const clone = card.cloneNode(true);
      clone.classList.remove('is-active', 'is-near');
      clone.classList.add('is-clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.querySelectorAll('img').forEach((image) => image.setAttribute('loading', 'eager'));
      clone.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    };

    const leadingClones = document.createDocumentFragment();
    originalCards.slice(-cloneCount).forEach((card) => leadingClones.append(makeClone(card)));
    track.insertBefore(leadingClones, track.firstChild);
    originalCards.slice(0, cloneCount).forEach((card) => track.append(makeClone(card)));

    const desktopCards = [...track.querySelectorAll('[data-collection-card]')];
    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
    const logicalIndexAt = (trackPosition) => ((trackPosition - cloneCount) % cardCount + cardCount) % cardCount;

    const updateDots = () => {
      dots.forEach((dot, index) => dot.setAttribute('aria-pressed', index === activeIndex ? 'true' : 'false'));
    };

    const updateDesktop = (animate = true) => {
      track.classList.toggle('is-jumping', !animate);
      desktopCards.forEach((card, index) => {
        const distance = Math.abs(index - position);
        card.classList.toggle('is-active', distance === 0);
        card.classList.toggle('is-near', distance === 1);
        card.classList.toggle('is-far-left', index === position - 2);
        card.classList.toggle('is-far-right', index === position + 2);
        card.classList.toggle('is-hidden-slide', distance > 2);
        card.style.zIndex = String(desktopCards.length - distance);
      });

      const activeCard = desktopCards[position];
      section.style.setProperty(
        '--collections-offset',
        `${viewport.clientWidth / 2 - activeCard.offsetLeft - activeCard.offsetWidth / 2}px`
      );

      updateDots();
    };

    const restoreTransitions = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => track.classList.remove('is-jumping'));
      });
    };

    const updateMobile = (scroll = true) => {
      originalCards.forEach((card, index) => {
        card.classList.toggle('is-active', index === activeIndex);
        card.classList.toggle('is-near', Math.abs(index - activeIndex) === 1);
        card.classList.remove('is-far-left', 'is-far-right', 'is-hidden-slide');
      });
      updateDots();
      if (scroll) {
        const activeCard = originalCards[activeIndex];
        viewport.scrollTo({
          left: activeCard.offsetLeft - (viewport.clientWidth - activeCard.offsetWidth) / 2,
          behavior: 'smooth'
        });
      }
    };

    const show = (index, scrollMobile = true) => {
      activeIndex = (index + cardCount) % cardCount;
      if (isMobile()) {
        updateMobile(scrollMobile);
        return;
      }
      if (logicalIndexAt(position) !== activeIndex) position = cloneCount + activeIndex;
      updateDesktop(false);
      restoreTransitions();
    };

    const settleLoop = () => {
      if (!animationLocked) return;
      window.clearTimeout(fallbackTimer);
      let needsSnap = false;
      if (position < cloneCount) {
        position += cardCount;
        needsSnap = true;
      }
      if (position >= cloneCount + cardCount) {
        position -= cardCount;
        needsSnap = true;
      }
      if (needsSnap) {
        updateDesktop(false);
        restoreTransitions();
      }
      animationLocked = false;
    };

    const moveBy = (delta) => {
      if (isMobile()) {
        show(activeIndex + delta);
        return;
      }
      if (animationLocked || delta === 0) return;
      animationLocked = true;
      position += delta;
      activeIndex = logicalIndexAt(position);
      updateDesktop(true);
      fallbackTimer = window.setTimeout(settleLoop, 750);
    };

    track.addEventListener('transitionend', (event) => {
      if (event.target === track && event.propertyName === 'transform') settleLoop();
    });

    section.querySelector('[data-collections-previous]')?.addEventListener('click', () => moveBy(-1));
    section.querySelector('[data-collections-next]')?.addEventListener('click', () => moveBy(1));
    dots.forEach((dot) => dot.addEventListener('click', () => {
      const target = Number(dot.dataset.collectionsDot);
      const forward = (target - activeIndex + cardCount) % cardCount;
      const backward = forward - cardCount;
      moveBy(Math.abs(backward) < forward ? backward : forward);
    }));

    const resizeObserver = new ResizeObserver(() => {
      if (isMobile()) updateMobile(false);
      else {
        position = cloneCount + activeIndex;
        updateDesktop(false);
        restoreTransitions();
      }
    });
    resizeObserver.observe(viewport);

    let scrollTimer;
    viewport.addEventListener('scroll', () => {
      if (!isMobile()) return;
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        const center = viewport.getBoundingClientRect().left + viewport.clientWidth / 2;
        const closest = originalCards.reduce((best, card, index) => {
          const bounds = card.getBoundingClientRect();
          const distance = Math.abs(bounds.left + bounds.width / 2 - center);
          return distance < best.distance ? { index, distance } : best;
        }, { index: activeIndex, distance: Infinity });
        activeIndex = closest.index;
        updateMobile(false);
      }, 100);
    }, { passive: true });

    show(activeIndex);
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll('[data-collections-showcase]').forEach(initialize);
  };

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true })
    : initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
